import React from 'react';
import Typography from 'new/elements/Typography';
import { AuthTemplate } from 'new/components/settings/VendorBuilder/AuthConfig/AuthTemplate';
import { Stack } from '@mui/material';
import { Button } from 'new/elements/Button';
import { AddRounded } from '@mui/icons-material';
import { Section } from 'new/components/settings/VendorBuilder/components/Section';
import { useVendorBuilder } from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';

interface AuthTemplatesProps {}

export const AuthTemplates = (props: AuthTemplatesProps) => {
  const { vendor, onAddTemplate } = useVendorBuilder();
  return (
    <Section
      title={
        <KnowledgePanel
          alwaysShowUnderline
          knowledgeContent={
            <div style={{ padding: 'var(--spacing-lg)' }}>
              <Typography variant="body2">
                <strong>Understanding the Auth Template Structure</strong>
                <br />
                This is how auth templates work. In each <code>auth_template</code>
                <ul>
                  <li>
                    <strong>Auth Template Basics: </strong>You set some basic information about the
                    template that allows user to understand what type of authentication.
                  </li>
                  <li>
                    <strong>Auth Template Basics: </strong>You choose type of authentication. This
                    adds some default entries in <code>auth_templates[].config</code> section. UI
                    doesn't care about this object.
                  </li>
                  <li>
                    <strong>Auth Parameters</strong>: You define properties that will be sent to and
                    from UI.
                  </li>
                  <li>
                    <strong>Auth Template Config: </strong>You customize so you have the exact{' '}
                    <code>auth_templates[].config</code> needed. UI doesn't care about this
                    object.{' '}
                  </li>
                  <li>
                    When user is creating/updating a credential,{' '}
                    <strong>API applies this equation</strong>: <code>params</code> from request
                    payload + <code>config</code> stored with the template = desired{' '}
                    <code>data_credential</code>
                  </li>
                  <li>
                    <strong>API Testing Info:</strong> Set rules that will be applied for validating
                    the credential. They also end up in <code>auth_templates[].config</code> object.
                  </li>
                </ul>
              </Typography>
            </div>
          }
        >
          <div>
            Auth Templates[<code>vendor.auth_templates</code>]
          </div>
        </KnowledgePanel>
      }
      titleAcessory={
        <Typography variant="body2">
          specs for <code>data_credential</code> templates
        </Typography>
      }
      gap="16px"
    >
      <Typography variant="body2">
        This section contains specs that are used for converting limited user input into{' '}
        <code>data_credentials</code>.
        <ul>
          <li>
            Each connector can have many auth_templates. That is to support API vendors who support
            multiple mechanisms of authentication.
          </li>
          <li>
            Each <code>auth_template</code> maps to one type of credential mechanism
          </li>
          <li>Typically API vendors write separate page about authentication their API docs.</li>
          <li>
            <strong>Recommendation:</strong> Pick only one (easiest to spec) when creating the
            template. Ex: API Key over OAuth.
          </li>
        </ul>
      </Typography>

      {vendor.auth_templates.map((template, index) => {
        return <AuthTemplate template={template} index={index} key={index} />;
      })}
      <Stack alignItems="flex-start">
        <Button onClick={onAddTemplate} startIcon={<AddRounded />}>
          Add template
        </Button>
      </Stack>
    </Section>
  );
};
